import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';

import { createPortal } from 'react-dom';

import { getContacts, bulkUploadContacts, createContact } from '../../api-wa/contactsApi';

import { getTemplates, sendCampaign, createTemplate } from '../../api-wa/templatesApi';

import { getCampaign, createCampaign, updateCampaign, launchCampaign, createDraftCampaign, launchCampaignNew } from '../../api-wa/campaignsApi';

import { getApiErrorMessage } from '../../api-wa/client';

import AddContactModal from '../../Components/WhatsApp/AddContactModal';

import { ToastContext } from '../../Context/WaToastContext';



const getRenderedPreview = (body, variables) => {

  if (!body) return '';

  let rendered = body;

  Object.keys(variables).forEach(v => {

    // Only process numerical variables like {{1}}, {{2}}

    if (isNaN(v)) return;



    const val = variables[v];

    const placeholder = `{{${v}}}`;

    const replacement = val && typeof val === 'string' && val.trim() ? val : placeholder;

    rendered = rendered.split(placeholder).join(replacement);

  });

  return rendered;

};



// WhatsApp text formatting: *bold*, _italic_, ~strikethrough~

const formatWhatsAppText = (text) => {

  if (!text) return '';

  // Split into parts, preserving formatting markers

  const parts = [];

  let remaining = text;

  const regex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;

  let lastIndex = 0;

  let match;

  while ((match = regex.exec(remaining)) !== null) {

    if (match.index > lastIndex) {

      parts.push(remaining.substring(lastIndex, match.index));

    }

    const raw = match[0];

    if (raw.startsWith('*') && raw.endsWith('*')) {

      parts.push(<strong key={match.index}>{raw.slice(1, -1)}</strong>);

    } else if (raw.startsWith('_') && raw.endsWith('_')) {

      parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);

    } else if (raw.startsWith('~') && raw.endsWith('~')) {

      parts.push(<s key={match.index}>{raw.slice(1, -1)}</s>);

    }

    lastIndex = match.index + raw.length;

  }

  if (lastIndex < remaining.length) {

    parts.push(remaining.substring(lastIndex));

  }

  return parts.length > 0 ? parts : text;

};



const CreateCampaign = ({ onBack, onSaved, onCreateTemplate, setIsDirty, closeAttempted, onCancelClose, onConfirmClose }) => {

  const { showError } = useContext(ToastContext);

  const [dbContacts, setDbContacts] = useState([]);

  const [customContacts, setCustomContacts] = useState([]); // {id, name, phone_number, source}

  const [loading, setLoading] = useState(true);

  const [sending, setSending] = useState(false);

  const [error, setError] = useState(null);

  const [sendStatus, setSendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'failed'

  const [sendError, setSendError] = useState(null);



  const [campaignName, setCampaignName] = useState('');

  const [selectedContactIds, setSelectedContactIds] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');

  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [scheduleTime, setScheduleTime] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);



  // Draft editing state

  const [editCampaignDraftId, setEditCampaignDraftId] = useState(null);

  const [savingDraft, setSavingDraft] = useState(false);

  const [isSavingDraftStatus, setIsSavingDraftStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const [originalStatus, setOriginalStatus] = useState('');





  // Template state

  const [templates, setTemplates] = useState([]);

  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [templateVariables, setTemplateVariables] = useState({}); // { '1': '', '2': '' }

  const [templateLoading, setTemplateLoading] = useState(false);

  const [headerMediaUrl, setHeaderMediaUrl] = useState('');

  const [headerMediaFile, setHeaderMediaFile] = useState(null);



  // Template Modal & Draft state

  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const [templateData, setTemplateData] = useState({ name: '', body: '', category: 'MARKETING' });

  const [isDrafting, setIsDrafting] = useState(false);

  const [showDraftBanner, setShowDraftBanner] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);



  // Paste Panel state

  const [pasteMode, setPasteMode] = useState(false);

  const [pastedText, setPastedText] = useState('');

  const [validationResult, setValidationResult] = useState({ valid: [], invalid: [] });



  const fileInputRef = useRef(null);

  const isLoadingDraftRef = useRef(false);

  // React state (`sending`/`sendStatus`) only disables the button after a re-render, which is too

  // late for a fast double-click/double-tap - both clicks land before the first setSending(true)

  // is reflected in the DOM, firing two campaign-create requests. This ref is checked synchronously.

  const isSubmittingRef = useRef(false);

  const [uploadType, setUploadType] = useState(null); // 'excel' or 'csv'

  const [showBulkPanel, setShowBulkPanel] = useState(false);

  const [bulkPanelTab, setBulkPanelTab] = useState('excel'); // 'excel' | 'csv' | 'paste'



  const [hasSaved, setHasSaved] = useState(false);

  const isDirty = useMemo(() => {

    if (hasSaved) return false;

    const nameEntered = campaignName.trim() !== '';

    const templateSelected = selectedTemplateId !== '';

    const contactsSelected = selectedContactIds.length > 0;

    const scheduleSet = scheduleTime !== '';

    const varsEntered = Object.values(templateVariables).some(v => v !== '');

    return nameEntered || templateSelected || contactsSelected || scheduleSet || varsEntered;

  }, [campaignName, selectedTemplateId, selectedContactIds, scheduleTime, templateVariables, hasSaved]);



  useEffect(() => {

    if (setIsDirty) {

      setIsDirty(isDirty);

    }

    return () => {

      if (setIsDirty) {

        setIsDirty(false);

      }

    };

  }, [isDirty, setIsDirty]);



  // Load draft on mount

  useEffect(() => {

    const savedDraft = localStorage.getItem('campaign_template_draft');

    if (savedDraft) {

      setTemplateData(JSON.parse(savedDraft));

      setShowDraftBanner(true);

    }



    const draftId = localStorage.getItem('editCampaignDraftId');

    if (draftId) {

      setEditCampaignDraftId(draftId);

      loadCampaignDraft(draftId);

    } else {

      fetchData();

    }

  }, []);



  useEffect(() => {

    if (isLoadingDraftRef.current) {

      return;

    }

    let autoMediaUrl = '';

    if (selectedTemplateId) {

      const template = templates.find(t => t.id.toString() === selectedTemplateId.toString());

      if (template) {

        if (template.body) {

          const matches = template.body.match(/\{\{(\d+)\}\}/g) || [];

          const vars = {};

          matches.forEach(m => {

            const num = m.replace(/\{\{|\}\}/g, '');

            vars[num] = '';

          });

          setTemplateVariables(prev => {

            const updated = { ...vars };

            Object.keys(updated).forEach(k => {

              if (prev[k] !== undefined && prev[k] !== '') {

                updated[k] = prev[k];

              }

            });

            return updated;

          });

        } else {

          setTemplateVariables({});

        }



        autoMediaUrl = 

          template.uploaded_preview_image ||

          template.meta_media_url ||

          template.header_media ||

          (template.sample_values && (template.sample_values.header_handle || template.sample_values.media_url)) ||

          '';

      }

    } else {

      setTemplateVariables({});

    }

    setHeaderMediaUrl(autoMediaUrl);

    setHeaderMediaFile(null);

  }, [selectedTemplateId, templates]);



  // Auto-save draft every 2 seconds

  useEffect(() => {

    let interval;

    if (showTemplateModal && (templateData.name || templateData.body)) {

      interval = setInterval(() => {

        localStorage.setItem('campaign_template_draft', JSON.stringify(templateData));

        setIsDrafting(true);

        setTimeout(() => setIsDrafting(false), 800);

      }, 2000);

    }

    return () => clearInterval(interval);

  }, [showTemplateModal, templateData]);



  // Build campaign payload

  const buildCampaignPayload = (isDraft = false, dateToSchedule = null, overrideContactIds = null) => {

    const nameToSave = campaignName.trim() || `Draft Campaign - ${new Date().toLocaleDateString()}`;

    const dbContactIds = overrideContactIds || selectedContactIds.filter(id => !String(id).startsWith('custom-'));

    const customContactsToSave = customContacts.filter(l => selectedContactIds.includes(l.id));



    const payloadVariables = { ...templateVariables };

    if (isDraft && customContactsToSave.length > 0) {

      payloadVariables['_custom_contacts'] = customContactsToSave;

    }



    const payload = {

      name: nameToSave,

      template_id: selectedTemplateId ? Number(selectedTemplateId) : null,

      contact_ids: dbContactIds,

      variables: payloadVariables

    };

    if (headerMediaUrl) {

      payload.variables['__header_media_url__'] = headerMediaUrl;

    }

    if (dateToSchedule) payload.scheduled_at = dateToSchedule;

    return payload;

  };



  // Background Auto-Save Effect disabled



  const loadCampaignDraft = async (draftId) => {

    try {

      setLoading(true);

      setError(null);

      isLoadingDraftRef.current = true;



      // Fetch templates and contacts first. page_size is explicit - without it the default

      // page cuts off well short of the full list, so matching a large campaign's contacts

      // against it silently drops most of them (backend caps at 10000, ContactPagination).

      const contactsData = await getContacts({ page_size: 10000 });

      const loadedContacts = Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []);

      setDbContacts(loadedContacts);



      setTemplateLoading(true);

      const templatesData = await getTemplates();

      setTemplates(templatesData);

      setTemplateLoading(false);



      // Fetch campaign draft

      const campaign = await getCampaign(draftId);

      if (campaign) {

        setOriginalStatus(campaign.status || '');

        setCampaignName(campaign.name || '');

        if (campaign.template) {

          // Handle both ID or object nested template

          const tId = typeof campaign.template === 'object' ? campaign.template.id : campaign.template;

          setSelectedTemplateId(tId?.toString() || '');

        }



        let loadedCustomContacts = [];

        if (campaign.variables) {

          const { _custom_contacts, __header_media_url__, ...cleanVars } = campaign.variables;



          setTemplateVariables(cleanVars);



          if (__header_media_url__) {

            setHeaderMediaUrl(__header_media_url__);

          }



          if (Array.isArray(_custom_contacts)) {

            loadedCustomContacts = _custom_contacts;

            setCustomContacts(_custom_contacts);

          }

        } else {

          setTemplateVariables({});

        }



        const dbPhones = Array.isArray(campaign.contacts) ? campaign.contacts : [];

        const matchedDbIds = dbPhones.map(phone => {

          const match = loadedContacts.find(c => {

            const cPhone = c.phone_number?.replace(/\D/g, '') || '';

            const queryPhone = phone.replace(/\D/g, '') || '';

            return cPhone.endsWith(queryPhone) || queryPhone.endsWith(cPhone);

          });

          return match ? match.id : null;

        }).filter(Boolean);

        const customIds = loadedCustomContacts.map(l => l.id);

        setSelectedContactIds(Array.from(new Set([...matchedDbIds, ...customIds])));



        if (campaign.scheduled_at) {

          try {

            setScheduleTime(campaign.scheduled_at.slice(0, 16));

          } catch (e) {

            console.error("Failed to parse scheduled_at:", e);

          }

        }

      }

    } catch (err) {

      setError(getApiErrorMessage(err, 'Failed to load campaign draft'));

    } finally {

      setLoading(false);

      setTemplateLoading(false);

      setTimeout(() => {

        isLoadingDraftRef.current = false;

      }, 500);

    }

  };



  const fetchData = async () => {

    try {

      setLoading(true);

      const contactsData = await getContacts({ page_size: 10000 });

      setDbContacts(Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []));



      setTemplateLoading(true);

      // Fetch all templates to show in the grid

      const templatesData = await getTemplates();

      setTemplates(templatesData);

    } catch (err) {

      setError(getApiErrorMessage(err, 'Failed to fetch data'));

    } finally {

      setLoading(false);

      setTemplateLoading(false);

    }

  };



  // Combine DB contacts and custom contacts

  const allContacts = useMemo(() => {

    return [...dbContacts, ...customContacts];

  }, [dbContacts, customContacts]);



  const filteredContacts = useMemo(() => {

    const term = searchTerm.toLowerCase().trim();

    if (!term) return allContacts;

    return allContacts.filter(l =>

      (l.name && l.name.toLowerCase().includes(term)) ||

      (l.phone_number && l.phone_number.includes(term))

    );

  }, [allContacts, searchTerm]);



  const { leftContacts, rightContacts } = useMemo(() => {

    const half = Math.ceil(filteredContacts.length / 2);

    return {

      leftContacts: filteredContacts.slice(0, half),

      rightContacts: filteredContacts.slice(half)

    };

  }, [filteredContacts]);



  const selectedTemplate = useMemo(() => {

    if (!selectedTemplateId || templates.length === 0) return null;

    return templates.find(t => t.id.toString() === selectedTemplateId.toString()) || null;

  }, [templates, selectedTemplateId]);



  // Contact toggling

  const handleToggleContact = (id) => {

    setSelectedContactIds(prev =>

      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]

    );

  };



  const handleSelectAll = () => {

    if (selectedContactIds.length === filteredContacts.length) {

      setSelectedContactIds([]);

    } else {

      setSelectedContactIds(filteredContacts.map(l => l.id));

    }

  };



  // --- Template Management ---

  const handleOpenTemplateModal = () => {

    if (onCreateTemplate) {

      onCreateTemplate();

      return;

    }

    setShowTemplateModal(true);

    setShowDraftBanner(false);

  };



  const handleCloseTemplateModal = () => {

    setShowTemplateModal(false);

    if (templateData.name || templateData.body) {

      setShowDraftBanner(true);

    }

  };



  const handleSubmitTemplate = async (e) => {

    e.preventDefault();

    if (!templateData.name || !templateData.body) return alert('Please fill all fields');



    try {

      setTemplateLoading(true);

      await createTemplate(templateData);

      setSuccessMessage('Template submitted for review!');

      localStorage.removeItem('campaign_template_draft');

      setTemplateData({ name: '', body: '', category: 'MARKETING' });

      setShowTemplateModal(false);

      setShowDraftBanner(false);

      fetchData(); // Refresh list

      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (err) {

      alert(getApiErrorMessage(err, 'Failed to create template'));

    } finally {

      setTemplateLoading(false);

    }

  };





  // --- Contact Upload Parsing ---

  const validatePhone = (num) => {

    const clean = num.toString().replace(/\D/g, '');

    return clean.length === 10 ? clean : null;

  };



  const processNumbers = (numbers, source) => {

    const valid = [];

    const invalid = [];



    numbers.forEach(num => {

      const cleaned = validatePhone(num);

      if (cleaned) valid.push(cleaned);

      else invalid.push(num);

    });



    const newContacts = valid.map(num => ({

      id: `custom-${source}-${num}-${Date.now()}-${Math.random()}`,

      name: `${num}`,

      phone_number: num,

      source: source.toUpperCase()

    }));



    setCustomContacts(prev => [...prev, ...newContacts]);

    setSelectedContactIds(prev => [...prev, ...newContacts.map(l => l.id)]);



    return { validCount: valid.length, invalidCount: invalid.length };

  };



  const handleFileUpload = (e) => {

    const file = e.target.files[0];

    if (!file) return;



    const reader = new FileReader();

    reader.onload = async (evt) => {

      if (uploadType === 'excel') {

        const mod = await import('xlsx');

        const XLSX = mod && (mod.default ?? mod);

        const wb = XLSX.read(evt.target.result, { type: 'binary' });

        const ws = wb.Sheets[wb.SheetNames[0]];

        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        const flat = data.flat().filter(cell => cell !== null && cell !== undefined);

        const result = processNumbers(flat, 'Excel');

        alert(`Imported ${result.validCount} valid numbers from Excel. ${result.invalidCount} were invalid.`);

      } else {

        const mod = await import('papaparse');

        const Papa = mod && (mod.default ?? mod);

        Papa.parse(file, {

          complete: (results) => {

            const flat = results.data.flat().filter(cell => cell);

            const result = processNumbers(flat, 'CSV');

            alert(`Imported ${result.validCount} valid numbers from CSV. ${result.invalidCount} were invalid.`);

          }

        });

      }

    };



    if (uploadType === 'excel') reader.readAsBinaryString(file);

    else reader.readAsText(file);



    e.target.value = null; // Reset input

  };



  // --- Paste Panel Logic ---

  const handleValidatePaste = () => {

    const lines = pastedText.split(/[\n,]+/).map(l => l.trim()).filter(l => l);

    const valid = [];

    const invalid = [];

    lines.forEach(line => {

      const cleaned = validatePhone(line);

      if (cleaned) valid.push(cleaned);

      else invalid.push(line);

    });

    setValidationResult({ valid, invalid });

  };



  const handleAddPasted = () => {

    processNumbers(validationResult.valid, 'Pasted');

    setPastedText('');

    setValidationResult({ valid: [], invalid: [] });

    setPasteMode(false);

  };



  const handleSaveDraft = async () => {

    if (!campaignName.trim()) {

      showError('Please enter a campaign name');

      return;

    }

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;



    try {

      setSending(true);

      setError(null);



      const contactsPhones = selectedContactIds.map(id => {

        const contact = allContacts.find(c => c.id === id);

        return contact ? (contact.phone || contact.phone_number) : null;

      }).filter(Boolean);



      const payload = {

        id: editCampaignDraftId ? Number(editCampaignDraftId) : null,

        name: campaignName.trim(),

        template_id: selectedTemplateId ? Number(selectedTemplateId) : null,

        contacts: contactsPhones,

        variables: templateVariables,

        scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null,

        status: 'DRAFT'

      };



      await createDraftCampaign(payload);

      setHasSaved(true);

      localStorage.removeItem('editCampaignDraftId');



      if (onSaved) onSaved();

    } catch (err) {

      showError(getApiErrorMessage(err, 'Failed to save draft'));

    } finally {

      setSending(false);

      isSubmittingRef.current = false;

    }

  };



  const handleModalSaveDraft = async () => {

    if (!campaignName.trim()) {

      showError('Please enter a campaign name');

      return;

    }

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;



    try {

      setSending(true);



      const contactsPhones = selectedContactIds.map(id => {

        const contact = allContacts.find(c => c.id === id);

        return contact ? (contact.phone || contact.phone_number) : null;

      }).filter(Boolean);



      const payload = {

        id: editCampaignDraftId ? Number(editCampaignDraftId) : null,

        name: campaignName.trim(),

        template_id: selectedTemplateId ? Number(selectedTemplateId) : null,

        contacts: contactsPhones,

        variables: templateVariables,

        scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null,

        status: 'DRAFT'

      };



      await createDraftCampaign(payload);

      setHasSaved(true);

      localStorage.removeItem('editCampaignDraftId');



      if (onConfirmClose) onConfirmClose();

    } catch (err) {

      showError(getApiErrorMessage(err, 'Failed to save draft'));

    } finally {

      setSending(false);

      isSubmittingRef.current = false;

    }

  };



  const handleModalSaveScheduled = async () => {

    if (!campaignName.trim()) {

      showError('Please enter a campaign name');

      return;

    }

    if (selectedContactIds.length === 0) {

      showError('Please select at least one contact');

      return;

    }

    if (!selectedTemplateId) {

      showError('Please select a template');

      return;

    }

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;



    try {

      setSending(true);



      const contactsPhones = selectedContactIds.map(id => {

        const contact = allContacts.find(c => c.id === id);

        return contact ? (contact.phone || contact.phone_number) : null;

      }).filter(Boolean);



      const payloadVariables = { ...templateVariables };

      if (headerMediaUrl) {

        payloadVariables['__header_media_url__'] = headerMediaUrl;

      }



      const payload = {

        id: editCampaignDraftId ? Number(editCampaignDraftId) : null,

        name: campaignName.trim(),

        template_id: selectedTemplateId ? Number(selectedTemplateId) : null,

        contacts: contactsPhones,

        variables: payloadVariables,

        scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null

      };



      await launchCampaignNew(payload);

      setHasSaved(true);

      localStorage.removeItem('editCampaignDraftId');



      if (onConfirmClose) onConfirmClose();

    } catch (err) {

      showError(getApiErrorMessage(err, 'Failed to save scheduled campaign'));

    } finally {

      setSending(false);

      isSubmittingRef.current = false;

    }

  };





  const handleModalDiscard = () => {

    setHasSaved(true);

    localStorage.removeItem('editCampaignDraftId');

    if (onConfirmClose) onConfirmClose();

  };



  const handleSendCampaign = async () => {

    if (!campaignName.trim()) {

      setSendError('Please enter a campaign name');

      return;

    }

    if (selectedContactIds.length === 0) {

      setSendError('Please select at least one contact');

      return;

    }

    if (!selectedTemplateId) {

      setSendError('Please select a template');

      return;

    }

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;



    try {

      setSendStatus('sending');

      setSendError(null);



      const contactsPhones = selectedContactIds.map(id => {

        const contact = allContacts.find(c => c.id === id);

        return contact ? (contact.phone || contact.phone_number) : null;

      }).filter(Boolean);



      const payloadVariables = { ...templateVariables };

      if (headerMediaUrl) {

        payloadVariables['__header_media_url__'] = headerMediaUrl;

      }



      const payload = {

        id: editCampaignDraftId ? Number(editCampaignDraftId) : null,

        name: campaignName.trim(),

        template_id: selectedTemplateId ? Number(selectedTemplateId) : null,

        contacts: contactsPhones,

        variables: payloadVariables,

        scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null

      };



      const result = await launchCampaignNew(payload);



      setSendStatus('sent');

      setSuccessMessage(

        originalStatus === 'SCHEDULED'

          ? `Scheduled campaign updated successfully!`

          : (scheduleTime

              ? `Campaign scheduled successfully for ${new Date(scheduleTime).toLocaleString()}!`

              : `Campaign launched successfully!`)

      );



      setHasSaved(true);

      localStorage.removeItem('editCampaignDraftId');



      setTimeout(() => {

        setSendStatus('idle');

        setSuccessMessage(null);

        if (onSaved) onSaved();

      }, 2000);



    } catch (err) {

      setSendStatus('failed');

      setSendError(getApiErrorMessage(err, 'Failed to launch campaign'));

    } finally {

      isSubmittingRef.current = false;

    }

  };



  if (loading) return (

    <div className="loading-state">

      <div className="premium-loader"></div>

      <p>Loading project resources...</p>

    </div>

  );



  return (

    <div className="create-campaign-page">

      <div className="premium-container">

        <div className="premium-card main-form">

          {successMessage && <div className="success-banner">{successMessage}</div>}



          <section className="form-section">

            <label className="premium-label">Campaign Identity</label>

            <input

              type="text"

              className="premium-input"

              placeholder="e.g. Summer Special Offer 2024"

              value={campaignName}

              onChange={e => setCampaignName(e.target.value)}

            />



            <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept={uploadType === 'excel' ? '.xlsx,.xls' : '.csv'} style={{ display: 'none' }} />



            {/* Mobile Inline Bulk Panel */}

            {showBulkPanel && (

              <div className="mobile-only-bulk-panel">

                <div className="bulk-panel-right">

                  <div className="bulk-panel-tabs">

                    {['excel', 'csv', 'paste'].map(tab => (

                      <button

                        key={tab}

                        className={`bulk-tab-btn ${bulkPanelTab === tab ? 'active' : ''}`}

                        onClick={() => setBulkPanelTab(tab)}

                      >

                        {tab === 'excel' ? '📊 Excel' : tab === 'csv' ? '📄 CSV' : '📋 Paste'}

                      </button>

                    ))}

                  </div>

                  {bulkPanelTab === 'excel' && (

                    <div className="bulk-panel-content">

                      <p className="panel-hint">Upload an Excel file (.xlsx or .xls) with phone numbers.</p>

                      <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('excel'); fileInputRef.current.click(); }}>

                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>

                        Choose Excel File

                      </button>

                    </div>

                  )}

                  {bulkPanelTab === 'csv' && (

                    <div className="bulk-panel-content">

                      <p className="panel-hint">Upload a CSV file with phone numbers.</p>

                      <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('csv'); fileInputRef.current.click(); }}>

                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>

                        Choose CSV File

                      </button>

                    </div>

                  )}

                  {bulkPanelTab === 'paste' && (

                    <div className="bulk-panel-content paste-view">

                      <p className="panel-hint">Paste phone numbers (one per line, with country code).</p>

                      <textarea className="paste-area" placeholder="e.g.&#10;919876543210&#10;919876543211" value={pastedText} onChange={e => setPastedText(e.target.value)} />

                      <div className="paste-actions">

                        <span className="live-count">{pastedText.split('\n').filter(l => l.trim()).length} numbers</span>

                        <button className="validate-btn" onClick={handleValidatePaste}>Validate</button>

                      </div>

                      {validationResult && (validationResult.valid.length > 0 || validationResult.invalid.length > 0) && (

                        <div className="validation-results">

                          <div className="res-item valid">✓ {validationResult.valid.length} Valid</div>

                          {validationResult.invalid.length > 0 && <div className="res-item invalid">✗ {validationResult.invalid.length} Invalid</div>}

                          <button className="premium-btn-primary add-pasted-btn" onClick={() => { handleAddPasted(); setShowBulkPanel(false); }}>Add {validationResult.valid.length} Contacts</button>

                        </div>

                      )}

                    </div>

                  )}

                </div>

              </div>

            )}

          </section>



          <section className="form-section templates-selection">

            <div className="section-header">

              <label className="premium-label">Select Template</label>

            </div>



            {showDraftBanner && (

              <div className="draft-banner">

                <span>You have an unsaved template draft.</span>

                <button onClick={handleOpenTemplateModal}>Continue Draft →</button>

              </div>

            )}



            <div className="template-scroll-container">

              <div className="template-grid">

                <div className="template-card create-card" onClick={handleOpenTemplateModal}>

                  <div className="plus-icon">+</div>

                  <span>Create Template</span>

                </div>

                {templates.map(t => (

                  <div

                    key={t.id}

                    className={`template-card ${selectedTemplateId === t.id.toString() ? 'selected' : ''} ${t.status === 'APPROVED' ? '' : 'disabled'}`}

                    onClick={() => t.status === 'APPROVED' && setSelectedTemplateId(t.id.toString())}

                  >

                    <div className="card-top">

                      <span className={`status-badge ${t.status.toLowerCase()}`}>{t.status}</span>

                      <span className="cat-tag">{t.category}</span>

                    </div>

                    <div className="card-name">{t.name}</div>

                    <div className="card-preview-text">{t.body.substring(0, 60)}...</div>

                  </div>

                ))}

              </div>

            </div>

          </section>



          <section className="form-section contacts-selection">

            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <label className="premium-label" style={{ margin: 0 }}>Select Recipients</label>

              <div className="selection-stats" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                <button

                  type="button"

                  className={`upload-btn bulk-upload-toggle ${showBulkPanel ? 'active' : ''}`}

                  onClick={() => setShowBulkPanel(v => !v)}

                  style={{

                    padding: '6px 12px',

                    height: '32px',

                    fontSize: '12px',

                    margin: 0,

                    borderRadius: '8px',

                    display: 'flex',

                    alignItems: 'center',

                    gap: '6px',

                    border: '1.5px solid transparent',

                    borderColor: showBulkPanel ? 'var(--brand-purple)' : 'var(--border-color)',

                    background: showBulkPanel ? 'var(--brand-purple)' : 'var(--surface-soft-2)',

                    color: showBulkPanel ? '#ffffff' : 'var(--brand-purple)',

                    fontWeight: '700',

                    cursor: 'pointer',

                    transition: 'all 0.2s',

                    boxShadow: 'none'

                  }}

                >

                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">

                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>

                    <polyline points="17 8 12 3 7 8"></polyline>

                    <line x1="12" y1="3" x2="12" y2="15"></line>

                  </svg>

                  Bulk Upload

                </button>

                <div style={{ width: '1px', height: '16px', background: 'var(--border-color)' }}></div>

                <span>{selectedContactIds.length} Selected</span>

                <button type="button" onClick={handleSelectAll} className="link-btn">

                  {selectedContactIds.length === filteredContacts.length && filteredContacts.length > 0 ? 'Deselect All' : 'Select All'}

                </button>

              </div>

            </div>



            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>

              <div 

                style={{ 

                  flex: 1, 

                  background: 'var(--bg-main)', 

                  border: '1.5px solid transparent', 

                  borderRadius: '10px', 

                  padding: '0 12px', 

                  display: 'flex', 

                  alignItems: 'center', 

                  height: '38px',

                  transition: 'all 0.2s ease',

                  overflow: 'hidden'

                }}

                onFocus={(e) => { e.currentTarget.style.border = '1.5px solid var(--brand-purple)'; e.currentTarget.style.background = 'var(--surface-white)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 77, 255, 0.08)'; }}

                onBlur={(e) => { e.currentTarget.style.border = '1.5px solid transparent'; e.currentTarget.style.background = 'var(--bg-main)'; e.currentTarget.style.boxShadow = 'none'; }}

              >

                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-gray)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>

                <input 

                  type="text" 

                  placeholder="Search by phone number..." 

                  value={searchTerm} 

                  onChange={e => {

                    const val = e.target.value.replace(/\D/g, '');

                    if (val.length <= 10) setSearchTerm(val);

                  }} 

                  style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', marginLeft: '8px', fontSize: '13px', color: 'var(--text-dark)', fontWeight: '600' }}

                />

              </div>



              <button 

                type="button"

                onClick={() => setShowAddModal(true)}

                style={{

                  background: 'var(--brand-purple)',

                  color: 'white',

                  border: 'none',

                  borderRadius: '10px',

                  height: '38px',

                  padding: '0 14px',

                  fontSize: '13px',

                  fontWeight: '700',

                  cursor: 'pointer',

                  boxShadow: '0 4px 12px rgba(124, 77, 255, 0.2)',

                  display: 'flex',

                  alignItems: 'center',

                  gap: '6px',

                  whiteSpace: 'nowrap',

                  transition: 'all 0.2s'

                }}

                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(124, 77, 255, 0.3)'; }}

                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 77, 255, 0.2)'; }}

              >

                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>

                Add Contact

              </button>

            </div>



            <div className="contacts-table-grid">

              {filteredContacts.length === 0 ? (

                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-gray)', fontSize: '13px', padding: '24px' }}>

                  No contacts found.

                </div>

              ) : (

                <>

                  <div className="contacts-column">

                    <div className="contacts-column-header">

                      <div className="checkbox-header-spacer" />

                      <span className="header-label name-lbl">Name</span>

                      <span className="header-label phone-lbl">Number</span>

                    </div>

                    {leftContacts.map(contact => {

                      const isSelected = selectedContactIds.includes(contact.id);

                      const cleanPhone = contact.phone_number?.replace(/\D/g, '') || '';

                      const displayPhone = cleanPhone.slice(-10);

                      const cleanName = contact.name?.replace(/\D/g, '') || '';

                      const displayName = contact.name && cleanName !== displayPhone ? contact.name : '—';

                      return (

                        <div

                          key={contact.id}

                          className={`contact-table-row ${isSelected ? 'selected' : ''}`}

                          onClick={() => handleToggleContact(contact.id)}

                        >

                          <div className="contact-grid-checkbox" style={{

                            borderColor: isSelected ? '#6366f1' : 'var(--border-color)',

                            background: isSelected ? '#6366f1' : 'transparent',

                            color: isSelected ? '#fff' : 'transparent',

                            marginRight: '8px'

                          }}>

                            {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}

                          </div>

                          <span className="contact-name-cell" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                            {displayName}

                          </span>

                          <span className="contact-phone-cell" style={{ fontFamily: 'monospace', marginLeft: 'auto', flexShrink: 0 }}>

                            {displayPhone}

                          </span>

                        </div>

                      );

                    })}

                  </div>

                  <div className="contacts-column">

                    <div className="contacts-column-header">

                      <div className="checkbox-header-spacer" />

                      <span className="header-label name-lbl">Name</span>

                      <span className="header-label phone-lbl">Number</span>

                    </div>

                    {rightContacts.map(contact => {

                      const isSelected = selectedContactIds.includes(contact.id);

                      const cleanPhone = contact.phone_number?.replace(/\D/g, '') || '';

                      const displayPhone = cleanPhone.slice(-10);

                      const cleanName = contact.name?.replace(/\D/g, '') || '';

                      const displayName = contact.name && cleanName !== displayPhone ? contact.name : '—';

                      return (

                        <div

                          key={contact.id}

                          className={`contact-table-row ${isSelected ? 'selected' : ''}`}

                          onClick={() => handleToggleContact(contact.id)}

                        >

                          <div className="contact-grid-checkbox" style={{

                            borderColor: isSelected ? '#6366f1' : 'var(--border-color)',

                            background: isSelected ? '#6366f1' : 'transparent',

                            color: isSelected ? '#fff' : 'transparent',

                            marginRight: '8px'

                          }}>

                            {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}

                          </div>

                          <span className="contact-name-cell" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                            {displayName}

                          </span>

                          <span className="contact-phone-cell" style={{ fontFamily: 'monospace', marginLeft: 'auto', flexShrink: 0 }}>

                            {displayPhone}

                          </span>

                        </div>

                      );

                    })}

                  </div>

                </>

              )}

            </div>

          </section>

        </div>



        {/* Right Panel */}

        <div className="premium-card side-panel">

          {showBulkPanel ? (

            <div className="bulk-panel-right">

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-dark)' }}>Bulk Upload</h3>

                <button

                  onClick={() => setShowBulkPanel(false)}

                  style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'var(--surface-soft-2)', color: 'var(--text-gray)', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}

                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}

                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-soft-2)'; e.currentTarget.style.color = 'var(--text-gray)'; }}

                >✕</button>

              </div>

              <div className="bulk-panel-tabs">

                {['excel', 'csv', 'paste'].map(tab => (

                  <button

                    key={tab}

                    className={`bulk-tab-btn ${bulkPanelTab === tab ? 'active' : ''}`}

                    onClick={() => setBulkPanelTab(tab)}

                  >

                    {tab === 'excel' ? '📊 Excel' : tab === 'csv' ? '📄 CSV' : '📋 Paste'}

                  </button>

                ))}

              </div>

              {bulkPanelTab === 'excel' && (

                <div className="bulk-panel-content">

                  <p className="panel-hint">Upload an Excel file (.xlsx or .xls) with phone numbers.</p>

                  <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('excel'); fileInputRef.current.click(); }}>

                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>

                    Choose Excel File

                  </button>

                </div>

              )}

              {bulkPanelTab === 'csv' && (

                <div className="bulk-panel-content">

                  <p className="panel-hint">Upload a CSV file with phone numbers column.</p>

                  <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('csv'); fileInputRef.current.click(); }}>

                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>

                    Choose CSV File

                  </button>

                </div>

              )}

              {bulkPanelTab === 'paste' && (

                <div className="bulk-panel-content">

                  <p className="panel-hint">Enter 10-digit numbers, separated by commas or lines.</p>

                  <textarea

                    className="paste-area"

                    placeholder={"Eg:- 9876543211,9876543210"}

                    value={pastedText}

                    onChange={e => setPastedText(e.target.value)}

                    style={{ height: '140px' }}

                  />

                  <div className="paste-actions">

                    <div className="live-count">{pastedText.split('\n').filter(l => l.trim()).length} numbers detected</div>

                    <button className="validate-btn" onClick={handleValidatePaste}>Validate</button>

                  </div>

                  {(validationResult.valid.length > 0 || validationResult.invalid.length > 0) && (

                    <div className="validation-results">

                      <div className="res-item valid">✓ Valid: {validationResult.valid.length} numbers</div>

                      <div className="res-item invalid">✗ Invalid: {validationResult.invalid.length} numbers</div>

                      {validationResult.valid.length > 0 && (

                        <button className="premium-btn-primary add-pasted-btn" onClick={() => { handleAddPasted(); setShowBulkPanel(false); }}>

                          Add {validationResult.valid.length} numbers →

                        </button>

                      )}

                    </div>

                  )}

                </div>

              )}

            </div>

          ) : (

            <div className="summary-view">

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>

                <h3 style={{ margin: 0 }}>Campaign Summary</h3>

                 <button

                  className="action-btn schedule-btn-premium"

                  style={{

                    display: 'flex', alignItems: 'center', gap: '4px',

                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',

                    color: '#fff', border: 'none', padding: '5px 10px',

                    borderRadius: '8px', cursor: 'pointer', fontWeight: '700',

                    fontSize: '11px', boxShadow: '0 2px 6px rgba(99,102,241,0.15)',

                    transition: 'all 0.2s', height: '28px'

                  }}

                  disabled={sending}

                  onClick={() => {

                    if (!campaignName.trim()) {

                      showError('Please enter a campaign name');

                      return;

                    }

                    if (!selectedTemplateId) {

                      showError('Please select a template');

                      return;

                    }

                    if (selectedContactIds.length === 0) {

                      showError('Please select at least one contact');

                      return;

                    }

                    setShowScheduleModal(true);

                  }}

                >

                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>

                  Schedule

                </button>

              </div>

              <div className="summary-list">

                <div className="summary-item">

                  <span className="label">Name</span>

                  <span className="value">{campaignName || 'Untitled Campaign'}</span>

                </div>

                <div className="summary-item">

                  <span className="label">Total Contacts</span>

                  <span className="value">{selectedContactIds.length}</span>

                </div>

              </div>



              {selectedTemplate && (

                <div className="template-preview-panel">

                  <label className="premium-label">Selected Template</label>

                  <div className="preview-card-compact" style={{ maxHeight: '400px', overflowY: 'auto' }}>

                    <div className="preview-header">

                      <span className="name">{selectedTemplate.name}</span>

                      <span className="badge">APPROVED</span>

                    </div>

                    {/* Compact Image Preview */}

                    {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.header_type) && (

                      <div className="compact-media-preview" style={{ marginBottom: '12px' }}>

                        {headerMediaUrl ? (

                          <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>

                            {selectedTemplate.header_type === 'IMAGE' && (

                              (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (

                                <img src={headerMediaUrl} alt="Preview" style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', display: 'block' }} />

                              ) : (

                                <div className="meta-handle-display-box" style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', borderRadius: '8px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>

                                  <span style={{ fontSize: '24px' }}>🖼️</span>

                                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Media Configured</span>

                                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>

                                </div>

                              )

                            )}

                            {selectedTemplate.header_type === 'VIDEO' && (

                              (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (

                                <video src={headerMediaUrl} style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', display: 'block' }} controls />

                              ) : (

                                <div className="meta-handle-display-box" style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', borderRadius: '8px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>

                                  <span style={{ fontSize: '24px' }}>🎥</span>

                                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Video Configured</span>

                                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>

                                </div>

                              )

                            )}

                            {selectedTemplate.header_type === 'DOCUMENT' && (

                              <div style={{ padding: '16px', background: 'var(--surface-soft)', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid var(--border-color)', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>

                                📄 <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)', wordBreak: 'break-all' }}>

                                  {headerMediaFile?.name || (headerMediaUrl.startsWith('http') ? 'Document Attached' : `Meta Sync Document: ${headerMediaUrl}`)}

                                </span>

                              </div>

                            )}

                            <button

                              onClick={() => { setHeaderMediaUrl(''); setHeaderMediaFile(null); }}

                              style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}

                            >✕</button>

                          </div>

                        ) : (

                          <div style={{ padding: '16px', border: '1.5px dashed var(--border-color)', borderRadius: '8px', textAlign: 'center', background: 'var(--surface-soft)' }}>

                            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-gray)', marginBottom: '8px' }}>

                              Header {selectedTemplate.header_type === 'IMAGE' ? 'Image' : selectedTemplate.header_type === 'VIDEO' ? 'Video' : 'Document'} Required

                            </div>

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>

                              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>Provide media URL:</span>

                              <input 

                                type="text"

                                placeholder="Paste Media URL"

                                style={{ 

                                  padding: '4px 8px', 

                                  borderRadius: '6px', 

                                  border: '1px solid #cbd5e1', 

                                  fontSize: '11px',

                                  outline: 'none',

                                  width: '150px'

                                }}

                                onChange={(e) => {

                                  setHeaderMediaUrl(e.target.value);

                                  setHeaderMediaFile(null);

                                }}

                              />

                            </div>

                          </div>

                        )}

                      </div>

                    )}

                    <div className="preview-body message-body" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{formatWhatsAppText(getRenderedPreview(selectedTemplate.body, templateVariables))}</div>

                    {/* Render Template Buttons if they exist */}

                    {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (

                      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

                        {selectedTemplate.buttons.map((btn, idx) => (

                          <div key={idx} style={{ 

                            padding: '8px 12px', 

                            background: '#f1f5f9', 

                            border: '1px solid var(--border-color)', 

                            borderRadius: '8px',

                            textAlign: 'center',

                            fontSize: '13px',

                            fontWeight: '600',

                            color: '#3b82f6',

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'center',

                            gap: '6px'

                          }}>

                            {btn.type === 'URL' ? (

                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>

                            ) : btn.type === 'PHONE_NUMBER' ? (

                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>

                            ) : (

                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 10 4 15 9 20"></polyline><path d="M20 4v7a4 4 0 0 1-4 4H4"></path></svg>

                            )}

                            {btn.text}

                          </div>

                        ))}

                      </div>

                    )}

                  </div>



                  {/* Dynamic Variables Section */}

                  {Object.keys(templateVariables).filter(k => !isNaN(k)).length > 0 && (

                    <div className="variables-section" style={{ marginTop: '16px' }}>

                      <label className="var-section-label">PERSONALIZE MESSAGE</label>

                      <div className="var-inputs">

                        {Object.keys(templateVariables)

                          .filter(k => !isNaN(k))

                          .sort((a, b) => parseInt(a) - parseInt(b))

                          .map(v => (

                            <div key={v} className="var-field">

                              <span className="var-pill">{"{{"}{v}{"}}"}</span>

                              <input

                                type="text"

                                className="premium-input var-input"

                                placeholder={`Value for var ${v}...`}

                                value={templateVariables[v]}

                                onChange={(e) => setTemplateVariables(prev => ({ ...prev, [v]: e.target.value }))}

                              />

                            </div>

                          ))}

                      </div>

                    </div>

                  )}

                </div>

              )}



              <div className="campaign-actions-container" style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

                <button

                  className="premium-btn-secondary"

                  style={{

                    width: '100%',

                    padding: '16px',

                    margin: 0,

                    fontWeight: '700',

                    cursor: sending ? 'not-allowed' : 'pointer'

                  }}

                  disabled={sending || !campaignName.trim()}

                  onClick={handleSaveDraft}

                >

                  {sending ? 'Saving...' : 'Save Draft'}

                </button>



                <button

                  className={`premium-btn-primary send-btn ${sendStatus === 'sending' ? 'loading' : ''}`}

                  style={{

                    width: '100%',

                    padding: '16px',

                    margin: 0,

                    opacity: sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId ? 0.6 : 1,

                    cursor: sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId ? 'not-allowed' : 'pointer'

                  }}

                  disabled={sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId}

                  onClick={handleSendCampaign}

                >

                  {sendStatus === 'sending' ? (

                    <>

                      <span style={{ display: 'inline-block', marginRight: '8px' }}>⏳</span>

                      Sending to {selectedContactIds.length} contacts...

                    </>

                  ) : sendStatus === 'sent' ? (

                    <>

                      <span style={{ display: 'inline-block', marginRight: '8px' }}>✓</span>

                      Campaign Sent Successfully!

                    </>

                  ) : (

                    editCampaignDraftId

                      ? (originalStatus === 'SCHEDULED' ? `Save Scheduled Campaign →` : `Save Changes →`)

                      : (scheduleTime 

                          ? `Schedule for ${selectedContactIds.length} contacts →` 

                          : `Send to ${selectedContactIds.length} contacts →`)

                  )}

                </button>



                {/* Error Message Display */}

                {sendError && (

                  <div style={{

                    marginTop: '12px',

                    padding: '12px 14px',

                    background: '#fee2e2',

                    border: '1px solid #fecaca',

                    borderRadius: '8px',

                    color: '#dc2626',

                    fontSize: '13px',

                    fontWeight: '500',

                    display: 'flex',

                    alignItems: 'center',

                    gap: '8px'

                  }}>

                    <span>✗</span>

                    <span>{sendError}</span>

                    <button

                      onClick={() => setSendError(null)}

                      style={{

                        marginLeft: 'auto',

                        background: 'none',

                        border: 'none',

                        color: '#dc2626',

                        cursor: 'pointer',

                        fontSize: '16px',

                        padding: 0

                      }}

                    >

                      ✕

                    </button>

                  </div>

                )}



                {/* Success Message Display */}

                {successMessage && (

                  <div style={{

                    marginTop: '12px',

                    padding: '12px 14px',

                    background: '#dcfce7',

                    border: '1px solid #86efac',

                    borderRadius: '8px',

                    color: '#16a34a',

                    fontSize: '13px',

                    fontWeight: '500',

                    display: 'flex',

                    alignItems: 'center',

                    gap: '8px',

                    animation: 'slideDown 0.3s ease-out'

                  }}>

                    <span>✓</span>

                    <span>{successMessage}</span>

                  </div>

                )}

              </div>

            </div>

          )}

        </div>

      </div>



      {/* Template Modal */}

      {showTemplateModal && createPortal(

        <div className="wa-module">

          <div className="wa-modal-overlay" onClick={handleCloseTemplateModal}>

            <div className="wa-modal-content premium-card" onClick={e => e.stopPropagation()}>

              <div className="wa-modal-header">

                <h2>{localStorage.getItem('campaign_template_draft') ? 'Continue Draft' : 'Create New Template'}</h2>

                {isDrafting && <span className="drafting-indicator">Auto-saving...</span>}

                <button className="close-modal" onClick={handleCloseTemplateModal}>✕</button>

              </div>

              <form onSubmit={handleSubmitTemplate}>

                <div className="wa-modal-body">

                  <div className="input-group">

                    <label className="premium-label">Template Name</label>

                    <input

                      type="text"

                      className="premium-input"

                      placeholder="e.g. welcome_offer"

                      value={templateData.name}

                      onChange={e => setTemplateData({ ...templateData, name: e.target.value })}

                    />

                  </div>

                  <div className="input-group">

                    <label className="premium-label">Category</label>

                    <select

                      className="premium-select"

                      value={templateData.category}

                      onChange={e => setTemplateData({ ...templateData, category: e.target.value })}

                    >

                      <option value="MARKETING">Marketing</option>

                      <option value="UTILITY">Utility</option>

                      <option value="AUTHENTICATION">Authentication</option>

                    </select>

                  </div>

                  <div className="input-group">

                    <label className="premium-label">Body Content</label>

                    <textarea

                      className="premium-textarea"

                      placeholder="Type your message here..."

                      value={templateData.body}

                      onChange={e => setTemplateData({ ...templateData, body: e.target.value })}

                    />

                    <span className="hint-text">Variables like {"{{1}}"}, {"{{2}}"} are supported.</span>

                  </div>

                </div>

                <div className="wa-modal-footer">

                  <button type="button" className="premium-btn-secondary" onClick={handleCloseTemplateModal}>Cancel</button>

                  <button type="submit" className="premium-btn-primary" disabled={templateLoading}>

                    {templateLoading ? 'Submitting...' : 'Submit to Meta for Review'}

                  </button>

                </div>

              </form>

            </div>

          </div>

        </div>,

        document.body

      )}



      {/* Schedule Campaign Modal */}

      {showScheduleModal && createPortal(
        <div className="wa-module">
          <div style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',

          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,

          animation: 'fadeIn 0.2s ease-out',
        }}>

          <div style={{

            background: 'var(--surface-white)', padding: '30px', borderRadius: '24px',

            width: '100%', maxWidth: '460px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',

            border: '1px solid var(--border-color)'

          }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>

              <div style={{ background: 'var(--brand-purple-light)', padding: '10px', borderRadius: '12px', display: 'flex' }}>

                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>

              </div>

              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: 'var(--text-dark)' }}>Schedule Campaign</h2>

            </div>



            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: 'var(--text-gray)', lineHeight: '1.5' }}>

              Choose a future date and time to broadcast your messages automatically.

            </p>



            <div style={{ marginBottom: '24px' }}>

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: 'var(--text-gray)', marginBottom: '8px' }}>Select Date & Time</label>

              <input

                type="datetime-local"

                className="premium-input"

                value={scheduleTime}

                onChange={e => setScheduleTime(e.target.value)}

                min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}

                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid var(--border-color)', background: 'var(--surface-white)', fontSize: '14px', outline: 'none' }}

              />

            </div>



            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>

              <button

                style={{

                  background: 'var(--surface-soft)', color: 'var(--text-gray)', border: 'none',

                  padding: '12px 20px', borderRadius: '12px', fontWeight: '700',

                  fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s'

                }}

                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border-color)'}

                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface-soft)'}

                onClick={() => {

                  setShowScheduleModal(false);

                  setScheduleTime('');

                }}

                disabled={sending}

              >

                Cancel

              </button>

              <button

                style={{

                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',

                  color: '#ffffff', border: 'none', padding: '12px 24px',

                  borderRadius: '12px', fontWeight: '700', fontSize: '14px',

                  cursor: 'pointer', transition: 'all 0.2s',

                  boxShadow: '0 4px 12px rgba(99,102,241,0.2)'

                }}

                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 6px 16px rgba(99,102,241,0.3)'}

                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2)'}

                onClick={async () => {

                  await handleSendCampaign();

                  setShowScheduleModal(false);

                }}

                disabled={sending || !scheduleTime}

              >

                {sending ? 'Scheduling...' : 'Confirm Schedule'}

              </button>

            </div>
          </div>
        </div>
        </div>,
        document.body
      )}



      <AddContactModal 

        visible={showAddModal} 

        onClose={() => setShowAddModal(false)} 

        onSaved={async (createdContact) => {

          try {

            setLoading(true);

            const contactsData = await getContacts();

            const loadedContacts = Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []);

            setDbContacts(loadedContacts);

            if (createdContact?.id) {

              setSelectedContactIds(prev => [...prev, createdContact.id]);

            }

          } catch (err) {

            console.error("Failed to refresh contacts:", err);

          } finally {

            setLoading(false);

          }

        }} 

      />



      {closeAttempted && createPortal(

        <div className="wa-modal-overlay" style={{ zIndex: 11000 }}>

          <div className="modal-content premium-card" style={{ maxWidth: '400px', textAlign: 'center', padding: '24px' }}>

            {editCampaignDraftId ? (

              <>

                <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '12px', color: 'var(--text-dark)' }}>Save Changes to Campaign?</h3>

                <p style={{ fontSize: '14px', color: 'var(--text-gray)', marginBottom: '24px' }}>You have unsaved changes to this campaign.</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                  <button 

                    className="premium-btn-primary" 

                    style={{ width: '100%' }} 

                    onClick={['SCHEDULED', 'RUNNING'].includes(originalStatus?.toUpperCase()) ? handleModalSaveScheduled : handleModalSaveDraft}

                  >

                    {['SCHEDULED', 'RUNNING'].includes(originalStatus?.toUpperCase()) ? 'Save Changes' : 'Save'}

                  </button>

                  <button 

                    className="premium-btn-secondary discard-btn" 

                    style={{ 

                      width: '100%', 

                      background: 'var(--surface-soft-2)', 

                      color: '#ef4444', 

                      border: '1.5px solid #fecaca',

                      fontWeight: '700'

                    }} 

                    onClick={handleModalDiscard}

                  >

                    Discard

                  </button>

                  <button className="premium-btn-secondary" style={{ width: '100%' }} onClick={onCancelClose}>Cancel</button>

                </div>

              </>

            ) : (

              <>

                <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '12px', color: 'var(--text-dark)' }}>Save Campaign as Draft?</h3>

                <p style={{ fontSize: '14px', color: 'var(--text-gray)', marginBottom: '24px' }}>You have unsaved changes.</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                  <button className="premium-btn-primary" style={{ width: '100%' }} onClick={handleModalSaveDraft}>Save Draft</button>

                  <button 

                    className="premium-btn-secondary discard-btn" 

                    style={{ 

                      width: '100%', 

                      background: 'var(--surface-soft-2)', 

                      color: '#ef4444', 

                      border: '1.5px solid #fecaca',

                      fontWeight: '700'

                    }} 

                    onClick={handleModalDiscard}

                  >

                    Discard

                  </button>

                  <button className="premium-btn-secondary" style={{ width: '100%' }} onClick={onCancelClose}>Cancel</button>

                </div>

              </>

            )}

          </div>

        </div>,

        document.body

      )}



      <style dangerouslySetInnerHTML={{

        __html: `

        .create-campaign-page { 

          --text-gray: #000000;

          --text-muted: #000000;

          --text-dark: #000000;

          flex: 1; 

          display: flex; 

          flex-direction: column; 

          padding: 0; 

          animation: fadeIn 0.3s ease; 

          box-sizing: border-box; 

          min-height: 0; 

        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        

        .premium-header { display: flex; align-items: center; gap: var(--layout-gap); margin-bottom: var(--layout-gap); }

        .premium-header h1 { font-size: 24px; font-weight: 800; color: var(--text-dark); margin: 0; }

        .premium-back-btn { width: 40px; height: 40px; border-radius: var(--radius-sm); border: 1px solid var(--border-color); background: var(--surface-white); cursor: pointer; display: flex; align-items: center; justify-content: center; color: #dc2626; transition: all 0.2s; }

        .premium-back-btn:hover { background: #fee2e2; color: #dc2626; border-color: #fecaca; transform: translateX(-2px); }



        .premium-container { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: var(--layout-gap); flex: 1; min-height: 0; }

        @media (max-width: 1024px) {

          .premium-container { grid-template-columns: 1fr; }

          .premium-header { flex-direction: column; align-items: flex-start; gap: var(--layout-gap); }

          .side-panel .bulk-panel-right { display: none !important; }

          .mobile-only-bulk-panel { display: block; margin-top: var(--layout-gap); }

          .mobile-only-bulk-panel .bulk-panel-right { animation: fadeIn 0.2s ease; background: var(--surface-soft); border-radius: var(--radius-md); border: 1.5px solid var(--border-color); overflow: hidden; }

          .template-grid { overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: var(--layout-gap); }

          .template-card { scroll-snap-align: start; flex: 0 0 80%; }

        }

        @media (max-width: 768px) {

          .create-campaign-page { padding: 0 !important; }

        }

        @media (max-width: 480px) {

          .contacts-grid-container { grid-template-columns: 1fr; }

          .premium-card { padding: var(--container-padding); }

          .upload-btn { padding: 8px; font-size: 12px; }

          .premium-btn-primary { padding: 10px 16px; }

          .premium-btn-secondary { padding: 10px 16px; }

        }

        @media (min-width: 1025px) {

          .mobile-only-bulk-panel { display: none !important; }

        }

        .premium-card { background: var(--surface-white); border-radius: var(--container-radius); padding: var(--container-padding); box-shadow: var(--shadow-sm); border: 1px solid var(--border-color); overflow-y: auto; }

        

        .upload-buttons { display: flex; gap: var(--layout-gap); margin-top: var(--layout-gap); flex-wrap: wrap; }

        .upload-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px; border-radius: var(--radius-sm); border: 2px solid var(--surface-soft-2); background: var(--surface-white); color: #6366f1; font-weight: 700; font-size: 13px; cursor: pointer; transition: all 0.2s; }

        .upload-btn:hover, .upload-btn.active { background: #6366f1; color: #fff; border-color: #6366f1; transform: translateY(-2px); }

        .bulk-upload-toggle { flex: none; padding: 10px 16px; gap: 10px; }



        .bulk-panel { margin-top: var(--layout-gap); background: var(--surface-soft); border: 1.5px solid var(--border-color); border-radius: var(--radius-md); overflow: hidden; animation: fadeIn 0.2s ease; }

        .bulk-panel-tabs { display: flex; border-bottom: 1px solid var(--border-color); background: var(--surface-white); }

        .bulk-tab-btn { flex: 1; padding: 12px 8px; border: none; background: transparent; cursor: pointer; font-size: 12px; font-weight: 700; color: var(--text-gray); transition: all 0.2s; border-bottom: 2px solid transparent; margin-bottom: -1px; }

        .bulk-tab-btn.active { color: #6366f1; border-bottom-color: #6366f1; background: var(--brand-purple-light); }

        .bulk-panel-content { padding: var(--container-padding); display: flex; flex-direction: column; gap: var(--layout-gap); }

        .bulk-panel-right { animation: fadeIn 0.2s ease; }

        .bulk-panel-right .bulk-panel-tabs { border-radius: var(--radius-sm); overflow: hidden; border: 1.5px solid var(--border-color); margin-bottom: var(--layout-gap); }



        .premium-label { font-size: 11px; font-weight: 800; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: block; }

        .premium-input { width: 100%; padding: 14px 18px; border-radius: var(--radius-sm); border: 2px solid var(--border-color); background: var(--surface-soft); font-size: 15px; outline: none; transition: all 0.2s; }

        .premium-input:focus { border-color: #6366f1; background: var(--surface-white); box-shadow: 0 0 0 4px rgba(99,102,241,0.1); }



        .templates-selection { margin-top: var(--space-xl); width: 100%; max-width: 100%; overflow: hidden; }

        .template-scroll-container { position: relative; }

        .template-scroll-container::after {

          content: '→';

          position: absolute;

          right: 0;

          top: 0;

          height: 100%;

          width: 60px;

          background: linear-gradient(to right, transparent, var(--surface-white));

          display: flex;

          align-items: center;

          justify-content: flex-end;

          padding-right: 12px;

          color: #6366f1;

          font-size: 20px;

          font-weight: 900;

          pointer-events: none;

          opacity: 0.8;

        }



        .template-grid { 

          display: flex; 

          gap: 12px; 

          margin-top: 16px; 

          overflow-x: auto; 

          overflow-y: hidden; 

          padding: 8px 4px 16px 4px; 

          scrollbar-width: thin; 

          scrollbar-color: #6366f1 #f1f5f9; 

          -webkit-overflow-scrolling: touch; 

        }

        .template-grid::-webkit-scrollbar { height: 6px; }

        .template-grid::-webkit-scrollbar-track { background: var(--surface-soft-2); border-radius: 10px; }

        .template-grid::-webkit-scrollbar-thumb { background: #6366f1; border-radius: 10px; }

        

        .template-card { flex: 0 0 160px; height: 110px; background: var(--surface-white); border: 2px solid var(--border-color); border-radius: 14px; padding: 12px; cursor: pointer; transition: all 0.2s; position: relative; display: flex; flex-direction: column; }

        .template-card:hover:not(.disabled) { border-color: #6366f1; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }

        .template-card.selected { border-color: #6366f1; background: var(--brand-purple-light); box-shadow: 0 4px 12px rgba(99,102,241,0.1); }

        .template-card.disabled { opacity: 0.6; cursor: not-allowed; background: var(--surface-soft); }

        

        .create-card { border-style: dashed; align-items: center; justify-content: center; color: #6366f1; font-weight: 700; gap: 4px; }

        .create-card .plus-icon { font-size: 24px; line-height: 1; }

        .create-card span { font-size: 11px; }

        

        .card-top { display: flex; justify-content: space-between; margin-bottom: 6px; }

        .status-badge { font-size: 7px; font-weight: 800; padding: 1px 4px; border-radius: 3px; text-transform: uppercase; }

        .status-badge.approved { background: #dcfce7; color: #166534; }

        .status-badge.pending { background: #fef9c3; color: #854d0e; }

        .status-badge.rejected { background: #fee2e2; color: #991b1b; }

        .cat-tag { font-size: 7px; color: var(--text-muted); font-weight: 700; }

        .card-name { font-size: 12px; font-weight: 800; color: var(--text-dark); margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .card-preview-text { font-size: 9px; color: var(--text-gray); line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }



        .success-banner { background: #dcfce7; color: #166534; padding: 12px 20px; border-radius: 12px; font-weight: 700; margin-bottom: 20px; font-size: 14px; animation: slideDown 0.3s ease; }

        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }



        .draft-banner { background: var(--brand-purple-light); border: 1px solid var(--brand-purple-light); padding: 12px 20px; border-radius: 12px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; font-size: 13px; font-weight: 600; color: #4338ca; }

        .draft-banner button { background: none; border: none; color: #6366f1; font-weight: 800; cursor: pointer; padding: 0; margin-left: 12px; }



        .contacts-selection { margin-top: var(--space-xl); }

        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }

        .selection-stats { font-size: 13px; font-weight: 700; color: var(--text-gray); display: flex; gap: 12px; align-items: center; }

        .link-btn { background: var(--surface-soft-2); border: none; padding: 4px 12px; border-radius: 8px; color: #6366f1; font-weight: 700; cursor: pointer; font-size: 12px; }

        

        .search-bar { display: flex; align-items: center; gap: 12px; background: var(--surface-soft-2); padding: 12px 16px; border-radius: 14px; margin-bottom: 16px; }

        .search-bar input { background: none; border: none; outline: none; width: 100%; font-size: 14px; }



        .contacts-table-grid {

          display: flex;

          gap: 24px;

          height: 434px; 

          overflow-y: auto; 

          background: var(--surface-white); 

          border: 1px solid var(--border-color);

          border-radius: var(--radius-md);

          padding: 0;

          box-sizing: border-box;

        }

        .contacts-column {

          flex: 1;

          display: flex;

          flex-direction: column;

        }

        .contacts-column-header {

          display: flex;

          align-items: center;

          height: 34px;

          border-bottom: 1px solid var(--border-color);

          position: sticky;

          top: 0;

          background: var(--brand-purple-light); /* light and clean purple/zinc background */

          padding: 0 16px;

          z-index: 10;

          font-size: 11px;

          font-weight: 800;

          color: var(--text-muted);

          text-transform: uppercase;

          letter-spacing: 0.05em;

          box-sizing: border-box;

        }

        .checkbox-header-spacer {

          width: 18px;

          margin-right: 8px;

          flex-shrink: 0;

        }

        .header-label.name-lbl {

          flex: 1;

        }

        .header-label.phone-lbl {

          margin-left: auto;

          flex-shrink: 0;

        }

        .contact-table-row {

          display: flex;

          align-items: center;

          height: 40px;

          border-bottom: 1px solid var(--border-color);

          cursor: pointer;

          transition: all 0.15s ease;

          font-size: 13px;

          color: var(--text-dark);

          font-weight: 600;

          box-sizing: border-box;

          padding: 0 16px;

        }

        .contact-table-row:hover {

          color: #6366f1;

        }

        .contact-table-row.selected {

          color: var(--text-dark);

        }

        .contact-table-row .contact-grid-checkbox { 

          width: 18px; 

          height: 18px; 

          border-radius: 6px; 

          border: 2px solid var(--border-color); 

          display: flex; 

          align-items: center; 

          justify-content: center; 

          transition: all 0.15s;

          flex-shrink: 0;

        }

        .contact-table-row.selected .contact-grid-checkbox {

          border-color: #6366f1;

          background: #6366f1;

        }

        .contact-table-row:hover .contact-grid-checkbox {

          border-color: #6366f1;

        }

        .contact-name-cell {

          font-weight: 700;

        }



        .side-panel { position: sticky; top: 20px; }

        .summary-view h3 { margin: 0 0 20px 0; font-size: 18px; font-weight: 800; color: var(--text-dark); }

        .summary-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }

        .summary-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: var(--surface-soft); border-radius: 12px; border: 1px solid var(--border-color); }

        .summary-item .label { font-size: 12px; color: var(--text-gray); font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }

        .summary-item .value { font-size: 14px; font-weight: 800; color: var(--text-dark); }

        

        .template-preview-panel { margin-top: 24px; border-top: 1px solid var(--border-color); padding-top: 24px; }

        .preview-card-compact { background: var(--surface-soft); border-radius: 18px 18px 18px 4px; padding: 16px; border: 1px solid var(--border-color); position: relative; margin-top: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }

        .preview-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid rgba(99,102,241,0.1); }

        .preview-header .name { font-size: 13px; font-weight: 800; color: var(--text-dark); }

        .preview-header .badge { font-size: 9px; font-weight: 800; color: #fff; background: #10b981; padding: 2px 8px; border-radius: 20px; text-transform: uppercase; }

        .preview-body { font-size: 13px; color: var(--text-dark); line-height: 1.6; white-space: pre-wrap; font-family: inherit; }

        .send-btn { width: 100%; margin-top: 24px; padding: 16px; font-size: 15px; box-shadow: 0 10px 15px -3px rgba(99,102,241,0.3); }



        .variables-section { margin-top: 24px; animation: fadeIn 0.3s ease; }

        .var-section-label { font-size: 11px; font-weight: 800; color: var(--text-muted); letter-spacing: 0.05em; display: block; margin-bottom: 12px; }

        .var-inputs { display: flex; flex-direction: column; gap: 10px; }

        .var-field { display: flex; align-items: center; gap: 10px; background: var(--surface-white); padding: 6px; border-radius: 12px; border: 1.5px solid var(--border-color); transition: all 0.2s; }

        .var-field:focus-within { border-color: #6366f1; box-shadow: 0 4px 12px rgba(99,102,241,0.05); }

        .var-pill { background: var(--brand-purple-light); color: #6366f1; font-family: monospace; font-weight: 800; font-size: 12px; padding: 6px 10px; border-radius: 8px; min-width: 45px; text-align: center; }

        .var-input { border: none !important; background: transparent !important; padding: 8px !important; font-size: 13px !important; box-shadow: none !important; }



        .paste-view { display: flex; flex-direction: column; gap: 20px; }

        .panel-header { display: flex; justify-content: space-between; align-items: center; }

        .panel-header h3 { margin: 0; font-size: 18px; font-weight: 800; color: var(--text-dark); }

        .close-panel { width: 32px; height: 32px; border-radius: 50%; border: none; background: var(--surface-soft-2); color: var(--text-gray); font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }

        .close-panel:hover { background: #fee2e2; color: #ef4444; }

        

        .panel-hint { font-size: 13px; color: var(--text-gray); line-height: 1.5; margin: 0; }

        .paste-area { width: 100%; height: 180px; padding: 16px; border-radius: 16px; border: 2px solid var(--border-color); background: var(--surface-soft); font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 13px; resize: none; transition: all 0.3s; outline: none; }

        .paste-area:focus { border-color: #6366f1; background: var(--surface-white); box-shadow: 0 10px 20px rgba(99,102,241,0.08); }

        

        .paste-actions { display: flex; justify-content: space-between; align-items: center; }

        .live-count { font-size: 12px; font-weight: 700; color: #6366f1; background: var(--brand-purple-light); padding: 4px 12px; border-radius: 20px; }

        .validate-btn { background: var(--surface-white); border: 2px solid #6366f1; color: #6366f1; padding: 8px 20px; border-radius: 10px; font-weight: 800; font-size: 13px; cursor: pointer; transition: all 0.2s; }

        .validate-btn:hover { background: #6366f1; color: #fff; }



        .validation-results { background: var(--surface-soft); border-radius: 16px; padding: 16px; border: 1px solid var(--border-color); display: flex; flex-direction: column; gap: 12px; animation: slideIn 0.3s ease; }

        @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        .res-item { font-size: 12px; font-weight: 700; }

        .res-item.valid { color: #10b981; }

        .res-item.invalid { color: #ef4444; }

        .add-pasted-btn { width: 100%; padding: 12px; font-size: 13px; margin-top: 4px; }



        .modal-overlay { 

          --text-gray: #000000;

          --text-muted: #000000;

          --text-dark: #000000;

          position: fixed; 

          top: 0; 

          left: 0; 

          right: 0; 

          bottom: 0; 

          background: rgba(0,0,0,0.4); 

          backdrop-filter: blur(4px); 

          display: flex; 

          align-items: center; 

          justify-content: center; 

          z-index: 1000; 

        }

        .modal-content { 

          --text-gray: #000000;

          --text-muted: #000000;

          --text-dark: #000000;

          width: 100%; 

          max-width: 500px; 

          animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); 

        }

        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

        .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }

        .modal-header h2 { font-size: 20px; font-weight: 800; margin: 0; }

        .drafting-indicator { font-size: 10px; color: #6366f1; font-weight: 700; margin-left: 12px; font-style: italic; }

        .close-modal { background: none; border: none; font-size: 24px; color: #dc2626; cursor: pointer; transition: color 0.2s; }

        .close-modal:hover { color: #b91c1c; }

        .input-group { margin-bottom: 16px; }

        .template-body-area { height: 120px; resize: none; font-family: inherit; }

        .hint { font-size: 10px; color: var(--text-muted); margin-top: 4px; }

        .modal-footer { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; }



        .premium-btn-primary { background: #6366f1; color: #fff; border: none; padding: 12px 24px; border-radius: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; }

        .premium-btn-primary:hover:not(:disabled) { background: #4f46e5; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(99,102,241,0.3); }

        .premium-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

        .premium-btn-secondary { background: var(--surface-soft-2); color: var(--text-gray); border: none; padding: 12px 24px; border-radius: 12px; font-weight: 700; cursor: pointer; }



        .premium-loader { width: 30px; height: 30px; border: 3px solid var(--border-color); border-top-color: var(--brand-purple); border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 12px; }

        @keyframes spin { to { transform: rotate(360deg); } }

        .loading-state { height: 300px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-gray); font-weight: 600; }

      `}} />

    </div>

  );

};



export default CreateCampaign;







